import type { SourceSpecification, GeoJSONSource, Map as MapboxMap } from "mapbox-gl";

export class MapBoxSource {
	public readonly id: string;
	private spec: SourceSpecification;
	private map: MapboxMap | null = null;
	private lastData: GeoJSON.FeatureCollection | null = null;

	constructor(opts: { id: string; spec: SourceSpecification }) {
		this.id = opts.id;
		this.spec = opts.spec;
	}

	attach = (map: MapboxMap) => {
		// Subscribe-once semantics. Each MapStore.onLoad triggers multiple attach
		// calls (source itself, then via MarkersStore.attach, then via ClusterMarkersStore.attach).
		// If we re-subscribed every time, the source listener would end up *after* the
		// layer listeners in mapbox's queue — layers would fire first on style.load,
		// see no source, and bail, leaving markers blank after a background change.
		if (this.map === map) {
			if (!map.getSource(this.id)) {
				map.addSource(this.id, this.spec);
			}
			if (this.lastData) this.applyData(this.lastData);
			return;
		}
		if (this.map) {
			this.map.off("style.load", this.handleStyleLoad);
		}
		this.map = map;

		if (!map.getSource(this.id)) {
			map.addSource(this.id, this.spec);
		}
		if (this.lastData) this.applyData(this.lastData);

		// mapbox wipes sources on setStyle — re-add ourselves whenever the style reloads.
		map.on("style.load", this.handleStyleLoad);
	};

	detach = () => {
		if (this.map) {
			this.map.off("style.load", this.handleStyleLoad);
		}
		this.map = null;
	};

	/** Update GeoJSON data (only works if this source is GeoJSON). Cached for re-apply on style change. */
	setGeoJSONData = (fc: GeoJSON.FeatureCollection) => {
		this.lastData = fc;
		this.applyData(fc);
	};

	/** Replace the spec. Detach + re-attach if changing source type. */
	setSpec = (spec: SourceSpecification) => {
		this.spec = spec;
	};

	private applyData = (fc: GeoJSON.FeatureCollection) => {
		if (!this.map) return;
		const src = this.map.getSource(this.id);
		if (src && "setData" in (src as any)) {
			(src as GeoJSONSource).setData(fc);
		}
	};

	private handleStyleLoad = () => {
		if (!this.map) return;
		if (!this.map.getSource(this.id)) {
			this.map.addSource(this.id, this.spec);
		}
		if (this.lastData) {
			this.applyData(this.lastData);
		}
	};
}
